import React from 'react';
import { FaList, FaCalendarAlt, FaCogs, FaClock } from 'react-icons/fa';

function WhyUs() {
  const reasonsLeft = [
    {
      title: 'Quick Turnaround Time',
      description: 'We ensure that you get your loan approvals within 1hr of application and disbursement within 30min of compliance',
      icon: <FaClock className="text-primary-red text-3xl mb-4 group-hover:text-primary-red-dark transition-colors duration-300" />,
    },
    {
      title: 'Simplified Loan Processing',
      description: 'We make access to finances simple and hustle free',
      icon: <FaList className="text-primary-red text-3xl mb-4 group-hover:text-primary-red-dark transition-colors duration-300" />,
    },
  ];

  const reasonsRight = [
    {
      title: 'Flexible Repayment Terms',
      description: 'We offer flexible repayment terms to suit your cashflow',
      icon: <FaCalendarAlt className="text-primary-red text-3xl mb-4 group-hover:text-primary-red-dark transition-colors duration-300" />,
    },
    {
      title: 'Customized Loan Products',
      description: 'Our products are tailor made to suit your specific personal and business needs as well as cashflows',
      icon: <FaCogs className="text-primary-red text-3xl mb-4 group-hover:text-primary-red-dark transition-colors duration-300" />,
    },
  ];

  return (
    <section className="bg-gray-50 py-16 sm:py-20" aria-label="Why Choose TGL Credit">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <h2 className="text-3xl font-inter font-bold text-center mb-4 tracking-tight animate-fade-up">
          <span style={{ color: '#2F498C' }}>Why</span>{' '}
          <span style={{ color: '#843345', borderBottom: '2px solid #843345' }}>Us</span>
        </h2>
        <p
          className="text-base font-inter font-medium text-gray-600 text-center mb-6 animate-fade-up"
          style={{ animationDelay: '0.15s' }}
        >
          At TGL Credit, we are committed to providing exceptional loan services to help you meet your financial needs.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr] gap-8">
          <div className="space-y-6">
            {reasonsLeft.map((reason, index) => (
              <div
                key={reason.title}
                className="group relative bg-white/95 backdrop-blur-md p-6 rounded-md shadow-md hover:shadow-lg hover:-translate-y-1 hover:scale-105 transition-all duration-300 border border-primary-blue/10 animate-fade-up"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                {reason.icon}
                <h3 className="text-lg font-inter font-bold text-primary-blue mb-2 tracking-tight">
                  {reason.title}
                </h3>
                <p className="text-[13px] font-inter font-medium text-gray-600 leading-relaxed">
                  {reason.description}
                </p>
                <div className="absolute inset-0 bg-primary-blue/10 opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-md" />
              </div>
            ))}
          </div>
          <div className="flex flex-col animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <img
              src="/assets/images/trade/loanprocessing.png"
              alt="Loan Processing"
              className="w-full h-full min-h-0 object-cover rounded-md shadow-md"
              loading="lazy"
              onError={() => console.error('Failed to load image: /assets/images/trade/loanprocessing.png')}
            />
          </div>
          <div className="space-y-6">
            {reasonsRight.map((reason, index) => (
              <div
                key={reason.title}
                className="group relative bg-white/95 backdrop-blur-md p-6 rounded-md shadow-md hover:shadow-lg hover:-translate-y-1 hover:scale-105 transition-all duration-300 border border-primary-blue/10 animate-fade-up"
                style={{ animationDelay: `${0.5 + index * 0.1}s` }}
              >
                {reason.icon}
                <h3 className="text-lg font-inter font-bold text-primary-blue mb-2 tracking-tight">
                  {reason.title}
                </h3>
                <p className="text-[13px] font-inter font-medium text-gray-600 leading-relaxed">
                  {reason.description}
                </p>
                <div className="absolute inset-0 bg-primary-blue/10 opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>
        {`
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-up {
            animation: fade-up 0.6s ease-out forwards;
            willChange: transform, opacity;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-up, .hover\\:scale-105, .hover\\:shadow-lg, .group-hover\\:opacity-50, .group-hover\\:text-primary-red-dark {
              animation: none !important;
              transform: none !important;
              opacity: 1 !important;
              transition: none !important;
            }
          }
          :root {
            --primary-blue: #2F498C;
            --primary-red: #843345;
            --primary-red-dark: #672737;
          }
          .bg-primary-blue {
            background-color: var(--primary-blue);
          }
          .bg-primary-red {
            background-color: var(--primary-red);
          }
          .text-primary-blue {
            color: var(--primary-blue);
          }
          .text-primary-red {
            color: var(--primary-red);
          }
          .text-primary-red-dark {
            color: var(--primary-red-dark);
          }
          .hover\\:bg-primary-blue:hover {
            background-color: var(--primary-blue);
          }
          .hover\\:bg-primary-red:hover {
            background-color: var(--primary-red);
          }
          .hover\\:text-primary-red:hover {
            color: var(--primary-red);
          }
          .hover\\:text-primary-red-dark:hover {
            color: var(--primary-red-dark);
          }
          .hover\\:shadow-primary-red\\/30:hover {
            --tw-shadow-color: rgba(132, 51, 69, 0.3);
            --tw-shadow: var(--tw-shadow-colored);
          }
          .border-primary-blue\\/10 {
            border-color: rgba(47, 73, 140, 0.1);
          }
          .group-hover\\:text-primary-red-dark:hover {
            color: var(--primary-red-dark);
          }
        `}
      </style>
    </section>
  );
}

export default WhyUs;